import { DictType } from "@pma/types"
import { useMutation } from "@tanstack/react-query"
import { Eye, EyeOff, Loader, ThumbsUp, Trash } from "lucide-react"
import { Dispatch, SetStateAction, useState } from "react"
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"
import {
  deleteMessageById,
  likeMessage,
  markMessageAsRead,
  markMessageAsUnRead,
  unLikeMessage,
} from "@pma/utils/utils"
import { useAuth } from "@stores/auth-store"
import { useNavigate, useSearchParams } from "react-router-dom"
import { cn } from "@pma/utils/helpers"
interface NotificationCardProps {
  revalidate: CallableFunction
  isRead?: boolean
  setShowOptions: Dispatch<SetStateAction<boolean>>
  likes: Array<DictType>
  notification: DictType
}
const NotificationActions = ({
  setShowOptions,
  isRead,
  likes,
  notification,
  revalidate,
}: NotificationCardProps) => {
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()
  const isHebrew = locale === "he"

  const userId = authConfig?.id
  const initialLiked = likes.some((like) => like?.id === userId)

  const [likeCount, setLikeCount] = useState(likes.length)
  const [isLiked, setIsLiked] = useState(initialLiked)

  const changeNotificationStatus = async () => {
    let response

    if (notification._is_read) {
      response = await markMessageAsUnRead([notification.id])
    } else {
      response = await markMessageAsRead([notification.id])
    }

    return response.data
  }

  const changeNotificationStatusMutation = useMutation({
    mutationKey: ["change-notification-status"],
    mutationFn: () => changeNotificationStatus(),
    onSuccess: (data) => {
      revalidate && revalidate()
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Unable to perform this action"),
        variant: "error",
      })
    },
  })

  const deleteNotificationMutation = useMutation({
    mutationKey: ["delete-notification"],
    mutationFn: () => deleteMessageById([notification.id]),
    onSuccess: (data) => {
      revalidate && revalidate()
      newParams.set("revalidate", "logs-notifications")
      navigate(`?${newParams.toString()}`)

      toast({
        title: translate("Success"),
        description: translate("Notification Deleted"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Unable to perform this action"),
        variant: "error",
      })
    },
  })

  const reactToNotification = async (like: boolean) => {
    let response

    if (like) {
      response = await unLikeMessage(notification.id)
    } else {
      response = await likeMessage(notification.id)
    }

    return response.data
  }

  const reactToNotificationMutation = useMutation({
    mutationKey: ["react-to-notification"],
    mutationFn: (like: boolean) => reactToNotification(like),
    onMutate: () => {
      const prevLiked = isLiked
      const prevCount = likeCount

      setIsLiked(!prevLiked)
      setLikeCount(prevLiked ? Math.max(0, prevCount - 1) : prevCount + 1)

      return { prevLiked, prevCount }
    },
    onSuccess: () => {
      revalidate && revalidate()
    },
    onError: (error: DictType, _variables, context) => {
      if (context) {
        setIsLiked(context.prevLiked)
        setLikeCount(context.prevCount)
      }
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Unable to perform this action"),
        variant: "error",
      })
    },
  })

  return (
    <div
      className={`absolute bottom-[8px] flex w-[100px] items-center justify-evenly ${isHebrew ? "left-0" : "right-0"}`}
      onMouseEnter={() => setShowOptions(true)}
    >
      {reactToNotificationMutation.isPending ? (
        <Loader className='animate-spin text-primary' size={16} />
      ) : (
        <ThumbsUp
          className={cn("cursor-pointer", isLiked ? "text-black" : "text-primary")}
          onClick={() => reactToNotificationMutation.mutate(isLiked)}
          size={16}
          fill={isLiked ? "blue" : "none"}
        />
      )}
      {isLiked ? <small> {likeCount || ""}</small> : null}
      {isRead ? (
        <Eye
          className='cursor-pointer text-xs text-black'
          onClick={() => changeNotificationStatusMutation.mutate()}
          size={16}
        />
      ) : (
        <EyeOff
          className='cursor-pointer text-black'
          onClick={() => changeNotificationStatusMutation.mutate()}
          size={16}
        />
      )}

      <Trash
        className='cursor-pointer text-red-400'
        onClick={() => deleteNotificationMutation.mutate()}
        size={16}
      />
    </div>
  )
}

export default NotificationActions
